import Quill from "quill";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import { uid } from "uid/single";
import { defineMessage, useIntl } from "react-intl";
import {
  textEditorStyle,
  labelStyle,
  labelTextStyle,
  labelDetailsStyle,
  labelTextWrapperStyle,
  messageContainerStyle,
  counterStyle,
  overlayStyle,
} from "./text-editor.css";
import "./text-editor-classes.css";
import { errorMessageIconStyle, errorMessageStyle } from "./input-base.css";
import { Text } from "@vagastec/ensemble-core";
import { InfoIcon } from "../icons/info-icon.js";
import {
  infoMessageIconStyle,
  infoMessageStyle,
} from "./input-info-message.css";
import { getCookie } from "../cookies";
import { Spinner } from "./spinner";
import PlainClipboard from "../plain-clipboard";
import { vars } from "../theme.css";

const TOKEN = getCookie(process.env.ACCESS_TOKEN);

export const TextEditor = React.forwardRef(
  (
    {
      label = "",
      labelDetails = "",
      placeholder = "",
      isImageHidden = false,
      isVideoHidden = false,
      isVariablesHidden = false,
      value = "",
      onChange = {},
      isValid,
      errorMessage,
      shouldValidate,
      infoMessage,
      imageUploadUrl,
      onImageUploadError = () => {},
      length = 0,
      id = "",
      onImageInsertError = () => {},
      variables = {
        defaultOption: "Variáveis",
        options: [
          "{{CANDIDATO_NOME}}",
          "{{CANDIDATO_EMAIL}}",
          "{{VAGA_CARGO}}",
          "{{VAGA_CODIGO}}",
        ],
      },
      ...rest
    },
    ref
  ) => {
    const intl = useIntl();
    const editorContainerRef = React.useRef();
    const toolbarContainerRef = React.useRef();
    const idRef = React.useRef(uid());
    const [isAfterBlur, setIsAfterBlur] = React.useState(false);
    // const [length, setLength] = React.useState(0);
    const quillInstanceRef = React.useRef();

    function color() {
      document.getElementById;
    }

    const [isImageUploadPending, setIsImageUploadPending] =
      React.useState(false);

    React.useEffect(() => {
      const quill = quillInstanceRef.current;
      if (value === editorContainerRef.current?.firstChild?.innerHTML || !quill)
        return;
      const delta = quill.clipboard.convert(value);
      quill.setContents(delta);
    }, [value]);

    React.useEffect(() => {
      Quill.register(
        {
          "modules/clipboard": PlainClipboard,
        },
        true
      );

      const quill = new Quill(editorContainerRef.current, {
        modules: {
          toolbar: {
            container: toolbarContainerRef.current,
            handlers: {
              custom: variablesChange,
            },
          },
          keyboard: {
            bindings: {
              tab: {
                key: 9,
                handler: () => true,
              },
            },
          },
        },
        placeholder,
        theme: "snow",
      });

      function variablesChange(value) {
        const quill = quillInstanceRef.current;
        quill.focus();
        const range = quill.getSelection();
        quill.insertText(range.index, value);
        onChange(editorContainerRef.current.firstChild.innerHTML);
      }

      function handleTextChange(delta, oldDelta, source) {
        // setLength(quill.getLength() - 1);
        // if (source === "user") {
        onChange(editorContainerRef.current.firstChild.innerHTML);
        // }
      }

      quill.on("text-change", handleTextChange);

      // editor-change: Emitted when either text-change or selection-change would be emitted, even when the source is "silent"
      // Adicionado para disparar o onChange no paste
      quill.on("editor-change", handleTextChange);

      editorContainerRef.current.firstChild.id = idRef.current;

      function handleSelectionChange(range) {
        setIsAfterBlur(!range);
      }

      quill.on("selection-change", handleSelectionChange);

      const delta = quill.clipboard.convert(value);
      quill.setContents(delta);

      quill.getModule("toolbar").addHandler("image", () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/png, image/jpg, image/jpeg");

        input.click();

        input.onchange = async function () {
          const [file] = input.files;
          const reader = new FileReader();

          reader.readAsDataURL(file);
          reader.addEventListener("load", () => {
            const img = new Image();
            img.onload = () => {
              if (img.width > 2000 || img.height > 1000) {
                onImageInsertError();
                return;
              }

              setIsImageUploadPending(true);
              fetch(imageUploadUrl, {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${TOKEN}`,
                },
                method: "post",
                body: JSON.stringify({ imagem: reader.result }),
              })
                .then((res) => res.json())
                .then((json) => {
                  const range = quill.getSelection();
                  quill.insertEmbed(range.index, "image", json.imagem_url);

                  onChange(editorContainerRef.current.firstChild.innerHTML);
                })
                .catch((error) => onImageUploadError(error))
                .finally(() => setIsImageUploadPending(false));
            };
            img.src = reader.result;
          });
        };
      });

      quillInstanceRef.current = quill;

      return () => {
        quill.off("text-change", handleTextChange);
        quill.off("selection-change", handleSelectionChange);
      };
    }, []);

    return (
      <>
        <label className={labelStyle} htmlFor={idRef.current}>
          <span className={labelTextWrapperStyle}>
            <span className={labelTextStyle}>{label}</span>
            {labelDetails ? (
              <span className={labelDetailsStyle}> ({labelDetails})</span>
            ) : null}
          </span>
        </label>
        <div
          className={textEditorStyle}
          ref={ref}
          {...rest}
          data-error={!isValid && (shouldValidate || isAfterBlur)}
        >
          {isImageUploadPending ? (
            <div className={overlayStyle}>
              <Spinner />
            </div>
          ) : null}
          <div>
            <div id="toolbar-container" ref={toolbarContainerRef}>
              <span className="ql-formats">
                <select className="ql-header" defaultValue="3">
                  <option value="1">
                    {intl.formatMessage(
                      defineMessage({
                        id: "editor.titulo1",
                        defaultMessage: "Título 1",
                      })
                    )}
                  </option>
                  <option value="2">
                    {intl.formatMessage(
                      defineMessage({
                        id: "editor.titulo2",
                        defaultMessage: "Título 2",
                      })
                    )}
                  </option>
                  <option value="3">
                    {intl.formatMessage(
                      defineMessage({
                        id: "editor.tituloNormal",
                        defaultMessage: "Normal",
                      })
                    )}
                  </option>
                </select>
              </span>
              <span className="ql-formats">
                <button className="ql-bold" aria-label="negrito"></button>
                <button className="ql-italic" aria-label="itálico"></button>
              </span>
              <span className="ql-formats">
                <button
                  className="ql-list"
                  value="ordered"
                  aria-label="lista ordenada"
                ></button>
                <button
                  className="ql-list"
                  value="bullet"
                  aria-label="lista não ordenada"
                ></button>
              </span>
              <span className="ql-formats">
                <button
                  className="ql-link"
                  aria-label="adicionar link"
                ></button>
                {isImageHidden ? null : (
                  <button
                    className="ql-image"
                    aria-label="inserir imagem"
                  ></button>
                )}
                {isVideoHidden ? null : (
                  <button
                    className="ql-video"
                    aria-label="inserir vídeo"
                  ></button>
                )}
              </span>

              <span className="ql-formats">
                <button
                  className="ql-clean"
                  aria-label="limpar formatação"
                ></button>
              </span>

              {/* Se isVariablesHidden for passado para o componente, as variáveis não são exibidas, se nada for passado, o select é exibido */}
              {isVariablesHidden || !variables.options.length ? null : (
                <>
                  <select
                    ref={(node) => {
                      node && document.querySelector(".ql-custom span")
                        ? (document.querySelector(
                            ".ql-custom span"
                          ).dataset.content = variables.defaultOption)
                        : null;
                    }}
                    className="ql-custom"
                    id="variables"
                  >
                    {variables.options.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
            <div
              style={{ minHeight: "3.75rem" }}
              ref={editorContainerRef}
              id={id}
            ></div>
          </div>
        </div>
        <div className={messageContainerStyle}>
          {errorMessage && !isValid && (shouldValidate || isAfterBlur) ? (
            <div className={errorMessageStyle}>
              <svg
                width={16}
                height={16}
                viewBox="0 0 24 24"
                fill="currentColor"
                className={errorMessageIconStyle}
              >
                <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm4.207 12.793l-1.414 1.414L12 13.414l-2.793 2.793-1.414-1.414L10.586 12 7.793 9.207l1.414-1.414L12 10.586l2.793-2.793 1.414 1.414L13.414 12l2.793 2.793z" />
              </svg>
              <Text size="sm" weight={600} as="p">
                {errorMessage}
              </Text>
            </div>
          ) : infoMessage ? (
            <div className={infoMessageStyle}>
              <InfoIcon
                width={16}
                height={16}
                className={infoMessageIconStyle}
              ></InfoIcon>
              <Text size="sm" weight={600} as="p">
                {infoMessage}
              </Text>
            </div>
          ) : null}
          <div style={{ flex: 1 }}></div>
          <div className={counterStyle}>
            <Text size="sm" as="p" fontColor={vars.color.foreground}>
              {length} caractere{length === 1 ? null : "s"}
            </Text>
          </div>
        </div>
      </>
    );
  }
);

TextEditor.displayName = "TextEditor";
